import React, { useState, useEffect, Fragment, useContext } from 'react';
import { 
  FontAwesomeIcon 
} from '@fortawesome/react-fontawesome';
import {
  faTrash,
  faEnvelope,
  faSpinner,
  faSearch,
  faTimesCircle,
  faCalendarAlt,
  faClock,
  faUsers,
  faUtensils
} from '@fortawesome/free-solid-svg-icons';
import { Dialog, Transition } from '@headlessui/react';
import { db, collection, getDocs, deleteDoc, doc, updateDoc } from '../../firebase';
import axios from 'axios';
import SiteDataContext from '../../context/SiteDataContext';
import FullScreenLoader from '../../components/ui/FullScreenLoader';
import { toast } from 'react-hot-toast';

const CateringSubmissions = () => {
  // State Management
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [bccEmails, setBccEmails] = useState([]);
  const { data } = useContext(SiteDataContext);
  const businessEmail = data?.business?.contact?.email;

  // Fetch Submissions on Component Mount
  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "cateringFormSubmissions"));
        const data = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // Ensure we have a valid date by using the server timestamp or fallback to current date
          date: doc.data().date?.toDate?.() || doc.data().date || new Date()
        }));
        
        // Sort submissions by date in descending order (latest first)
        data.sort((a, b) => b.date - a.date);
        setSubmissions(data);
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      }
    };

    const fetchBccEmails = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "bccEmails"));
        const data = querySnapshot.docs.map(doc => doc.data().email);
        setBccEmails(data);
      } catch (err) {
        console.error("Error fetching BCC emails: ", err);
        setError(err.message);
      }
    };

    fetchSubmissions();
    fetchBccEmails();
  }, []);

  // Utility Functions
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));

    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  // Action Handlers
  const handleDelete = async (submission) => {
    if (window.confirm(`Are you sure you want to delete the submission from ${submission.name}?`)) {
      setIsDeleting(true);
      try {
        await deleteDoc(doc(db, "cateringFormSubmissions", submission.id));
        setSubmissions(submissions.filter(sub => sub.id !== submission.id));
        toast.success(`Submission from ${submission.name} deleted successfully.`, {
            position: 'top-right',
        });
      } catch (err) {
        setError(err.message);
        toast.error(err.message, {
            position: 'top-right',
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleSendEmail = async (submission) => {
    if (window.confirm(`Are you sure you want to send an email to ${submission.email}?`)) {
      setIsSendingEmail(true);
      try {
        const response = await axios.post(`https://formsubmit.co/ajax/${businessEmail}`, {
          _subject: 'New Catering Form Submission',
          name: submission.name,
          email: submission.email,
          phone: submission.phone || 'N/A',
          message: submission.message,
          _bcc: bccEmails.join(','),
          _template:"table"
        });

        if (response.status === 200) {
          // Update Firestore
          const submissionDocRef = doc(db, "cateringFormSubmissions", submission.id);
          await updateDoc(submissionDocRef, { emailed: true });

          // Update local state
          setSubmissions(prevSubmissions =>
            prevSubmissions.map(sub =>
              sub.id === submission.id ? { ...sub, emailed: true } : sub
            )
          );

          // Show success message
            toast.success(`Email sent to ${submission.email}`, {
                position: 'top-right',
            });
        } else {
          toast.error('Failed to send email. Please try again.', {
                position: 'top-right',
            });
        }
      } catch (error) {
        console.error('Error sending email:', error);
        toast.error('Error sending email. Please try again.', {
                position: 'top-right',
            });
      } finally {
        setIsSendingEmail(false);
      }
    }
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "cateringFormSubmissions"));
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Ensure we have a valid date by using the server timestamp or fallback to current date
        date: doc.data().date?.toDate?.() || doc.data().date || new Date()
      }));
      
      // Sort submissions by date in descending order (latest first)
      data.sort((a, b) => b.date - a.date);
      setSubmissions(data);
      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  // Filtering logic
  const filteredSubmissions = submissions.filter(submission => {
    const searchLower = searchTerm.toLowerCase();
    return (
      submission.name.toLowerCase().includes(searchLower) ||
      submission.email.toLowerCase().includes(searchLower) ||
      (submission.phone && submission.phone.toLowerCase().includes(searchLower)) ||
      (submission.message && submission.message.toLowerCase().includes(searchLower))
    );
  });

  // Render Loading State
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-4xl" />
      </div>
    );
  }

  // Render Error State
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <p className="text-red-700">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {isSendingEmail && <FullScreenLoader />}
      <div className="max-w-7xl mx-auto">
        {/* Header with Refresh Button */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Catering Submissions</h1>
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={isLoading ? faSpinner : faUtensils} 
                            spin={isLoading} 
                            className="mr-2" />
            Refresh
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative rounded-md shadow-sm max-w-lg">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5"
              placeholder="Search submissions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setSearchTerm('')}
              >
                <FontAwesomeIcon icon={faTimesCircle} className="text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {filteredSubmissions.length === 0 ? (
            <div className="text-center p-8 bg-white rounded-lg">
              <p className="text-gray-500">
                {submissions.length === 0 
                  ? 'No catering submissions found.' 
                  : 'No submissions match your search criteria.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSubmissions.map((submission) => (
                    <tr key={submission.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {submission.date instanceof Date 
                          ? submission.date.toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {submission.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {submission.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {submission.phone || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div 
                          className="max-w-xs overflow-hidden truncate cursor-pointer hover:text-blue-500"
                          onClick={() => setSelectedSubmission(submission)}
                        >
                          {submission.message}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleSendEmail(submission)}
                          className={`mr-4 ${submission.emailed ? 'text-green-600 cursor-not-allowed' : 'text-blue-600 hover:text-blue-900'}`}
                          disabled={submission.emailed || isSendingEmail}
                        >
                          {isSendingEmail && !submission.emailed ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <FontAwesomeIcon icon={faEnvelope} />
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(submission)}
                          className="text-red-600 hover:text-red-900"
                          disabled={isDeleting || isSendingEmail}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Submission Details Modal */}
        {selectedSubmission && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-8 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex justify-between items-center border-b border-gray-100 pb-4 mb-6">
                <h3 className="text-2xl font-semibold text-gray-800">Submission Details</h3>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="space-y-6">
                {/* Date Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center text-gray-600 mb-1">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="font-medium">Date Submitted</span>
                  </div>
                  <p className="text-gray-800 ml-7">
                    {selectedSubmission.date instanceof Date 
                      ? selectedSubmission.date.toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })
                      : 'N/A'}
                  </p>
                </div>

                {/* Contact Information Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Name */}
                    <div>
                      <div className="flex items-center text-gray-600 mb-1">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="font-medium">Name</span>
                      </div>
                      <p className="text-gray-800 ml-7">{selectedSubmission.name}</p>
                    </div>

                    {/* Email */}
                    <div>
                      <div className="flex items-center text-gray-600 mb-1">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="font-medium">Email</span>
                      </div>
                      <p className="text-gray-800 ml-7 break-all">{selectedSubmission.email}</p>
                    </div>

                    {/* Phone */}
                    <div>
                      <div className="flex items-center text-gray-600 mb-1">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className="font-medium">Phone</span>
                      </div>
                      <p className="text-gray-800 ml-7">{selectedSubmission.phone || 'N/A'}</p>
                    </div>

                    {/* Event Details */}
                    {selectedSubmission.eventType && (
                      <div>
                        <div className="flex items-center text-gray-600 mb-1">
                          <FontAwesomeIcon icon={faUtensils} className="w-5 h-5 mr-2 text-gray-600" />
                          <span className="font-medium">Event Type</span>
                        </div>
                        <p className="text-gray-800 ml-7">{selectedSubmission.eventType}</p>
                      </div>
                    )}

                    {/* Guest Count */}
                    {selectedSubmission.guestCount && (
                      <div>
                        <div className="flex items-center text-gray-600 mb-1">
                          <FontAwesomeIcon icon={faUsers} className="w-5 h-5 mr-2 text-gray-600" />
                          <span className="font-medium">Guest Count</span>
                        </div>
                        <p className="text-gray-800 ml-7">{selectedSubmission.guestCount}</p>
                      </div>
                    )}

                    {/* Pickup Date */}
                    {selectedSubmission.pickupDate && (
                      <div>
                        <div className="flex items-center text-gray-600 mb-1">
                          <FontAwesomeIcon icon={faCalendarAlt} className="w-5 h-5 mr-2 text-gray-600" />
                          <span className="font-medium">Pickup Date</span>
                        </div>
                        <p className="text-gray-800 ml-7">{selectedSubmission.pickupDate}</p>
                      </div>
                    )}

                    {/* Pickup Time */}
                    {selectedSubmission.pickupTime && (
                      <div>
                        <div className="flex items-center text-gray-600 mb-1">
                          <FontAwesomeIcon icon={faClock} className="w-5 h-5 mr-2 text-gray-600" />
                          <span className="font-medium">Pickup Time</span>
                        </div>
                        <p className="text-gray-800 ml-7">{formatTime(selectedSubmission.pickupTime)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Message Section */}
                {selectedSubmission.message && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center text-gray-600 mb-2">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                      <span className="font-medium">Message</span>
                    </div>
                    <div className="ml-7 bg-white p-4 rounded-lg border border-gray-100">
                      <p className="text-gray-800 whitespace-pre-wrap">{selectedSubmission.message}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="mt-8 flex justify-end space-x-3 border-t border-gray-100 pt-6">
                <button
                  onClick={() => {
                    handleSendEmail(selectedSubmission);
                    setSelectedSubmission(null);
                  }}
                  className={`px-6 py-2.5 flex items-center justify-center rounded-lg transition-all duration-200
                    ${selectedSubmission.emailed 
                      ? 'bg-green-500 text-white cursor-not-allowed opacity-50' 
                      : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                  disabled={selectedSubmission.emailed || isSendingEmail}
                >
                  {isSendingEmail ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                      Sending...
                    </>
                  ) : selectedSubmission.emailed ? (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Email Sent
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                      Send Email
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    handleDelete(selectedSubmission);
                    setSelectedSubmission(null);
                  }}
                  className="px-6 py-2.5 flex items-center justify-center rounded-lg bg-red-500 hover:bg-red-600 text-white transition-all duration-200"
                  disabled={isDeleting}
                >
                  <FontAwesomeIcon icon={faTrash} className="mr-2" />
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="px-6 py-2.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CateringSubmissions;